import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { useSubmitSupervisorFeedback } from "@/hooks/use-quarterly-reviews";
import { FormError } from "@/components/shared/form-error";
import type { QuarterlyReview } from "@/types/entities";

const supervisorFeedbackSchema = z.object({
  supervisor_feedback: z.string().min(1, "Feedback is required").max(10000),
});

type SupervisorFeedbackFormData = z.infer<typeof supervisorFeedbackSchema>;

interface SupervisorFeedbackFormProps {
  review: QuarterlyReview;
  canEdit: boolean;
}

export function SupervisorFeedbackForm({ review, canEdit }: SupervisorFeedbackFormProps) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const submitFeedback = useSubmitSupervisorFeedback(review.id);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<SupervisorFeedbackFormData>({
    resolver: zodResolver(supervisorFeedbackSchema),
    defaultValues: { supervisor_feedback: review.supervisor_feedback ?? "" },
  });

  const onSubmit = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirm = () => {
    const data = getValues();
    submitFeedback.mutate(data.supervisor_feedback, {
      onSuccess: () => setConfirmDialogOpen(false),
    });
  };

  const isFinalized = review.status === "finalized";
  const isPendingFeedback = review.status === "pending_supervisor_feedback";

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Supervisor Feedback</CardTitle>
        </CardHeader>
        <CardContent>
          {isFinalized ? (
            <p className="whitespace-pre-wrap text-sm">
              {review.supervisor_feedback ?? "No supervisor feedback"}
            </p>
          ) : isPendingFeedback && canEdit ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="supervisor-feedback">
                  Provide feedback on your direct report's performance
                </Label>
                <Textarea
                  id="supervisor-feedback"
                  placeholder="Provide constructive feedback, highlight strengths, suggest areas for growth..."
                  rows={8}
                  {...register("supervisor_feedback")}
                />
                <FormError message={errors.supervisor_feedback?.message} />
              </div>
              <Button type="submit" disabled={submitFeedback.isPending}>
                {submitFeedback.isPending ? "Finalizing..." : "Submit Feedback & Finalize"}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))] italic">
              {review.status === "pending_self_reflection"
                ? "Awaiting self-reflection before supervisor feedback"
                : "Awaiting supervisor feedback"}
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalize Quarterly Review</DialogTitle>
            <DialogDescription>
              Submitting your feedback will finalize this review. Once finalized,
              neither the self-reflection nor the supervisor feedback can be changed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={submitFeedback.isPending}>
              {submitFeedback.isPending ? "Finalizing..." : "Finalize Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
